/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Mail,
  Clock,
  User,
  FileText,
  Link,
  CheckCircle,
  XCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Eye,
  MousePointer,
  PenTool,
  Send,
  Trash2,
  MessageSquare,
  Phone,
} from 'lucide-react';
import { toast } from 'sonner';
import EmailComposeDialog from './EmailComposeDialog';
import ClientGmailHistory from './ClientGmailHistory';   // v3.18-1378 GMAIL-002 Phase 3
import { useAuth } from '../contexts/AuthContext';

// v3.15-907: force ET timezone for all communication timestamps.
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/New_York',
  });
};

const EventTypeBadge = ({ type }) => {
  const configs = {
    email_sent: {
      icon: Send,
      label: 'Email Sent',
      bg: 'bg-blue-100',
      text: 'text-blue-800',
      border: 'border-blue-300'
    },
    email_opened: {
      icon: Eye,
      label: 'Email Opened',
      bg: 'bg-green-100',
      text: 'text-green-800',
      border: 'border-green-300'
    },
    link_clicked: {
      icon: MousePointer,
      label: 'Link Clicked',
      bg: 'bg-amber-100',
      text: 'text-amber-800',
      border: 'border-amber-300'
    },
    proposal_signed: {
      icon: PenTool,
      label: 'Proposal Signed',
      bg: 'bg-purple-100',
      text: 'text-purple-800',
      border: 'border-purple-300'
    }
  };

  const config = configs[type] || configs.email_sent;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text} border ${config.border}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  if (status === 'sent') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-green-100 text-green-800">
        <CheckCircle className="h-3 w-3" />
        Sent
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-red-100 text-red-800">
        <XCircle className="h-3 w-3" />
        Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800">
      <Clock className="h-3 w-3" />
      Pending
    </span>
  );
};

const TimelineEvent = ({ event, clientId, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { type, timestamp, data } = event;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/clients/${clientId}/communication-events/${event.id}`);
      toast.success('Communication event deleted');
      setDeleteConfirmOpen(false);
      if (onDelete) onDelete();
    } catch (err) {
      console.error('Failed to delete:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const renderEventContent = () => {
    switch (type) {
      case 'email_sent':
        return (
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <EventTypeBadge type={type} />
              <span className="font-medium text-primary">{data.subject}</span>
              <StatusBadge status={data.status} />
            </div>
            <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" />
                From: {data.sender_name}
              </span>
              <span className="flex items-center gap-1">
                <Mail className="h-3 w-3" />
                To: {data.to_email}
              </span>
            </div>
            {data.template && data.template !== 'custom' && (
              <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                <FileText className="h-3 w-3" />
                Template: {data.template.replace(/_/g, ' ')}
              </span>
            )}
          </div>
        );

      case 'email_opened':
        return (
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <EventTypeBadge type={type} />
              {data.email_subject && (
                <span className="text-sm text-slate-600">"{data.email_subject}"</span>
              )}
            </div>
            {data.ip_address && (
              <div className="text-xs text-slate-400 mt-1">
                IP: {data.ip_address}
              </div>
            )}
          </div>
        );

      case 'link_clicked':
        return (
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <EventTypeBadge type={type} />
              {data.link_type === 'signing_link' && (
                <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded">
                  Signing Link
                </span>
              )}
            </div>
            {data.url && (
              <div className="text-xs text-slate-400 mt-1 truncate max-w-md">
                URL: {data.url}
              </div>
            )}
            {data.ip_address && (
              <div className="text-xs text-slate-400">
                IP: {data.ip_address}
              </div>
            )}
          </div>
        );

      case 'proposal_signed':
        return (
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <EventTypeBadge type={type} />
              {data.deal_number && (
                <span className="text-sm font-medium text-purple-700">
                  Deal: {data.deal_number}
                </span>
              )}
            </div>
            {data.printed_name && (
              <div className="text-sm text-slate-600 mt-1">
                Signed by: {data.printed_name}
              </div>
            )}
            {data.ip_address && (
              <div className="text-xs text-slate-400 mt-1">
                IP: {data.ip_address}
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="flex-1">
            <EventTypeBadge type={type} />
          </div>
        );
    }
  };

  return (
    <>
      <div className="relative pl-6 pb-4">
        {/* Timeline line */}
        <div className="absolute left-2 top-2 bottom-0 w-0.5 bg-slate-200"></div>
        
        {/* Timeline dot */}
        <div className={`absolute left-0 top-1.5 w-4 h-4 rounded-full border-2 bg-white
          ${type === 'email_sent' ? 'border-blue-500' : ''}
          ${type === 'email_opened' ? 'border-green-500' : ''}
          ${type === 'link_clicked' ? 'border-amber-500' : ''}
          ${type === 'proposal_signed' ? 'border-purple-500' : ''}
        `}></div>
        
        <div className="border rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow group">
          <div className="p-3">
            <div className="flex items-start justify-between gap-2">
              {renderEventContent()}
              <div className="flex items-center gap-2">
                <div className="text-xs text-slate-400 whitespace-nowrap">
                  {formatDate(timestamp)}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setDeleteConfirmOpen(true)}
                  data-testid={`delete-event-${event.id}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Communication Event?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this communication event. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const EmailItem = ({ email }) => {
  const [expanded, setExpanded] = useState(false);
  // v3.15-752: Inbound vs outbound direction label
  const isInbound = email.direction === 'inbound' || email.type === 'inbound_email';

  return (
    <div className="border rounded-lg overflow-hidden" data-testid={isInbound ? 'email-item-inbound' : 'email-item-outbound'}>
      <div 
        className={`p-4 cursor-pointer hover:bg-slate-100 transition-colors ${isInbound ? 'bg-emerald-50' : 'bg-slate-50'}`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Mail className={`h-4 w-4 ${isInbound ? 'text-emerald-600' : 'text-slate-500'}`} />
              {isInbound ? (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-emerald-600 text-white"
                  data-testid="email-inbound-label"
                >Inbound</span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-slate-200 text-slate-700">Outbound</span>
              )}
              <span className="font-medium text-primary">{email.subject}</span>
              <StatusBadge status={email.status} />
            </div>
            <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {isInbound ? `From: ${email.sender_email || email.sender_name}` : `From: ${email.sender_name}`}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatDate(email.timestamp)}
              </span>
            </div>
            {email.template && email.template !== 'custom' && (
              <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800">
                <FileText className="h-3 w-3" />
                Template: {email.template.replace(/_/g, ' ')}
              </span>
            )}
          </div>
          <div className="flex items-center">
            {expanded ? (
              <ChevronUp className="h-5 w-5 text-slate-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-slate-400" />
            )}
          </div>
        </div>
      </div>
      
      {expanded && (
        <div className="p-4 border-t bg-white">
          <div className="text-sm text-slate-600 mb-2">
            <strong>To:</strong> {email.to_email}
          </div>
          {email.signing_link && (
            <div className="text-sm text-slate-600 mb-2">
              <strong className="flex items-center gap-1">
                <Link className="h-3 w-3" />
                Signing Link:
              </strong>{' '}
              <a href={email.signing_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                {email.signing_link}
              </a>
            </div>
          )}
          {email.deal_id && (
            <div className="text-sm text-slate-600 mb-2">
              <strong>Related Deal:</strong> {email.deal_id}
            </div>
          )}
          <div className="mt-3 p-3 bg-slate-50 rounded-lg">
            <pre className="text-sm whitespace-pre-wrap font-sans">{email.message}</pre>
          </div>
          {email.status === 'failed' && email.error_message && (
            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              <strong>Error:</strong> {email.error_message}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ClientCommunications = ({ clientId, clientEmail, clientName, clientPhone, dealId = null, dealNumber = null, signingToken = null }) => {
  const { isInternal } = useAuth();
  const [emails, setEmails] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [smsMessage, setSmsMessage] = useState('');
  const [sendingSms, setSendingSms] = useState(false);
  const [viewMode, setViewMode] = useState('timeline'); // 'timeline' or 'emails'

  const fetchData = async () => {
    try {
      // Fetch emails
      let emailResponse;
      if (dealId) {
        emailResponse = await api.get(`/ptc-deals/${dealId}/emails`);
      } else if (clientId) {
        emailResponse = await api.get(`/clients/${clientId}/emails`);
      }
      setEmails(emailResponse?.data || []);
      
      // Fetch communication events (timeline) if clientId exists
      if (clientId) {
        try {
          const eventsResponse = await api.get(`/clients/${clientId}/communication-events`);
          setEvents(eventsResponse?.data || []);
        } catch (err) {
          console.error('Failed to fetch communication events:', err);
          setEvents([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch emails:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId || dealId) {
      fetchData();
    }
  }, [clientId, dealId]);

  const handleEmailSent = () => {
    fetchData();
  };

  const handleSendSms = async () => {
    if (!smsMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    if (!clientPhone) {
      toast.error('No phone number available for this client');
      return;
    }
    
    setSendingSms(true);
    try {
      await api.post('/sms/send', {
        phone_number: clientPhone,
        message: smsMessage,
        client_id: clientId,
      });
      toast.success('SMS sent successfully');
      setSmsDialogOpen(false);
      setSmsMessage('');
      fetchData(); // Refresh to show SMS in timeline
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send SMS');
    } finally {
      setSendingSms(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* v3.18-1378 GMAIL-002 Phase 3 — synchronized Gmail history for this client. */}
      <ClientGmailHistory clientId={clientId} />
      <Card className="mt-4">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Client Communications
            </CardTitle>
            <div className="flex items-center gap-2">
              {/* View Mode Toggle */}
              <div className="flex rounded-lg border overflow-hidden">
                <button
                  onClick={() => setViewMode('timeline')}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    viewMode === 'timeline' 
                      ? 'bg-primary text-white' 
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid="timeline-view-btn"
                >
                  Timeline
                </button>
                <button
                  onClick={() => setViewMode('emails')}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    viewMode === 'emails' 
                      ? 'bg-primary text-white' 
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid="emails-view-btn"
                >
                  Emails Only
                </button>
              </div>
              {/* Send Email/SMS - PTC Internal Only */}
              {isInternal() && (
                <>
                  <Button 
                    onClick={() => setComposeOpen(true)}
                    className="bg-accent hover:bg-gold-dark"
                    data-testid="send-email-btn"
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Send Email
                  </Button>
                  {/* v3.18-1377 GMAIL-002 Phase 1 — Send SMS button is
                      physically removed from the DOM (not CSS-hidden)
                      until SMS is fully implemented. Backend intact. */}
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {viewMode === 'timeline' ? (
            // Timeline View - shows all events
            events.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Mail className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No communication events yet</p>
                <Button 
                  variant="link" 
                  onClick={() => setComposeOpen(true)}
                  className="text-accent"
                >
                  Send the first email
                </Button>
              </div>
            ) : (
              <div className="space-y-1 pt-2">
                {events.map((event, index) => (
                  <TimelineEvent 
                    key={event.id || index} 
                    event={event} 
                    clientId={clientId}
                    onDelete={fetchData}
                  />
                ))}
              </div>
            )
          ) : (
            // Emails Only View
            emails.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Mail className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No emails sent to this client yet</p>
                <Button 
                  variant="link" 
                  onClick={() => setComposeOpen(true)}
                  className="text-accent"
                >
                  Send the first email
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {emails.map(email => (
                  <EmailItem key={email.id} email={email} />
                ))}
              </div>
            )
          )}
        </CardContent>
      </Card>

      <EmailComposeDialog
        open={composeOpen}
        onOpenChange={setComposeOpen}
        clientId={clientId}
        clientEmail={clientEmail}
        clientName={clientName}
        dealId={dealId}
        dealNumber={dealNumber}
        signingToken={signingToken}
        onEmailSent={handleEmailSent}
      />

      {/* SMS Dialog */}
      <Dialog open={smsDialogOpen} onOpenChange={setSmsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Send SMS
            </DialogTitle>
            <DialogDescription>
              Send an SMS to {clientName} at {clientPhone}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Message</Label>
            <Textarea
              value={smsMessage}
              onChange={(e) => setSmsMessage(e.target.value)}
              placeholder="Type your message..."
              rows={4}
              className="mt-2"
              data-testid="sms-message-input"
            />
            <p className="text-xs text-slate-500 mt-2">
              Message will be sent via Twilio and logged to the client's timeline.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSmsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendSms} disabled={sendingSms || !smsMessage.trim()}>
              {sendingSms ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send SMS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClientCommunications;
